import React, { useState, useRef } from "react";
import { Package } from "lucide-react";
import { CATEGORIES, symbolsInCategory, getSymbol } from "../lib/symbols";

/**
 * V1.9.2 — Floor-plan Symbol Palette.
 *
 * Expandable hover-pill in the top-left of the Plan canvas.
 *
 *   Resting:   small cyan "+ Symbols" pill.
 *   Hover:     pill expands into a 5-item category bar.
 *   Hover cat: a flyout drops below the category showing symbol thumbs.
 *   Drag:      pointerdown on a thumb → starts a native HTML5 drag with
 *              dataTransfer payload `symbol:<id>`.
 *
 * Drop handling lives on the FloorPlan2D canvas (it converts pageX/pageY
 * to design-space metres and inserts a {type, x, z, rotation_deg} entry).
 *
 * Props:
 *   onSymbolDragStart: (symbolId, evt) — fires when the user picks a
 *     thumb so the editor can decorate the cursor.
 */
export default function SymbolPalette({ onSymbolDragStart }) {
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setActiveCategory(null);
    }, 220);
  };

  const handleDragStart = (e, symbolId) => {
    e.dataTransfer.setData("application/x-blueprint-symbol", symbolId);
    e.dataTransfer.effectAllowed = "copy";
    // Generate a 1×1 transparent drag image so the user's cursor
    // doesn't show the thumbnail twice (we'll draw a preview on the
    // canvas via dragover).
    const img = new Image();
    img.src =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=";
    try {
      e.dataTransfer.setDragImage(img, 0, 0);
    } catch {
      // older browsers ignore — fine.
    }
    onSymbolDragStart?.(symbolId, e);
  };

  return (
    <div
      data-testid="symbol-palette"
      /* V2.0.4 — hidden on phones (≤640px); the palette relies on hover
         + drag-and-drop which is impractical on touch. Tablet+ keeps it
         visible. Light editing on mobile is chat + click-to-change-type. */
      className="absolute top-14 left-3 z-30 select-none hidden sm:block"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      {/* Resting pill */}
      <button
        type="button"
        data-testid="symbol-palette-pill"
        className={
          "inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest shadow-sm transition-colors " +
          (open
            ? "border-brass-500 bg-brass-950/40 text-brass-200"
            : "border-brass-700 bg-brass-50 text-brass-700 hover:bg-brass-100")
        }
      >
        <Package className="w-3 h-3" /> + Symbols
      </button>

      {/* Category bar (slides out on hover) */}
      {open && (
        <div
          data-testid="symbol-palette-categories"
          className="mt-1 flex items-stretch border border-zinc-700 bg-zinc-950/95 backdrop-blur shadow-[0_12px_36px_rgba(0,0,0,0.45)]"
        >
          {CATEGORIES.map((cat) => (
            <button
              type="button"
              key={cat.id}
              data-testid={`symbol-cat-${cat.id}`}
              onMouseEnter={() => {
                cancelClose();
                setActiveCategory(cat.id);
              }}
              aria-pressed={activeCategory === cat.id}
              className={
                "px-2.5 py-1.5 border-r border-zinc-700 last:border-r-0 font-mono text-[10px] uppercase tracking-widest transition-colors " +
                (activeCategory === cat.id
                  ? "bg-brass-600 text-zinc-950"
                  : "text-zinc-300 hover:bg-zinc-800")
              }
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Symbol flyout grid */}
      {open && activeCategory && (
        <div
          data-testid={`symbol-flyout-${activeCategory}`}
          className="mt-1 w-72 border border-zinc-700 bg-zinc-950/95 backdrop-blur shadow-[0_12px_36px_rgba(0,0,0,0.45)] p-2"
          onMouseEnter={cancelClose}
        >
          <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 pb-1.5 mb-1.5 border-b border-zinc-800">
            // {CATEGORIES.find((c) => c.id === activeCategory)?.label} · drag to place
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {symbolsInCategory(activeCategory).map((s) => (
              <div
                key={s.id}
                data-testid={`symbol-thumb-${s.id}`}
                draggable
                onDragStart={(e) => handleDragStart(e, s.id)}
                title={`${s.label} (${s.width_m}m × ${s.depth_m}m)`}
                className="cursor-grab active:cursor-grabbing border border-zinc-700 hover:border-brass-500 bg-zinc-50 hover:bg-brass-50 transition-colors p-1"
              >
                <SymbolThumb id={s.id} />
                <div className="font-mono text-[8px] uppercase tracking-wider text-zinc-700 leading-tight mt-0.5 text-center truncate">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Renders a 72px × 48px thumbnail SVG of any catalog symbol.
function SymbolThumb({ id }) {
  const sym = getSymbol(id);
  if (!sym) return null;
  const aspect = sym.width_m / sym.depth_m;
  // Fit into the thumb box keeping aspect ratio.
  let w, h;
  const maxW = 64, maxH = 40;
  if (aspect >= maxW / maxH) {
    w = maxW;
    h = maxW / aspect;
  } else {
    h = maxH;
    w = maxH * aspect;
  }
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={maxW}
      height={maxH}
      style={{ display: "block", margin: "0 auto" }}
      preserveAspectRatio="xMidYMid meet"
    >
      <g transform={`translate(${(maxW - w) / 2}, ${(maxH - h) / 2})`}>
        {sym.render({ w, h })}
      </g>
    </svg>
  );
}
